import React, { Fragment } from "react";
import Layout from "../layout";

const PrivacyPolicy = () => {
  return (
    <Fragment>
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/30 to-pink-50/30 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Header Section */}
            <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-purple-100 p-8 md:p-12 mb-8">
              <div className="text-center mb-8">
                <div className="inline-block p-4 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full mb-6">
                  <svg className="w-16 h-16 text-purple-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h1 className="text-4xl md:text-5xl font-black bg-blue-800 from-purple-800 via-pink-700 to-red-600 bg-clip-text text-white mb-4">
                  Politique de confidentialité
                </h1>
                <p className="text-gray-600 text-lg font-medium">
                  Dernière mise à jour : <span className="text-purple-600 font-bold">15 novembre 2024</span>
                </p>
              </div>

              {/* Introduction */}
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  Chez <span className="font-bold text-purple-600">JamaalStore</span>, nous nous engageons à protéger votre vie privée. 
                  Cette politique explique comment nous collectons, utilisons et protégeons vos données personnelles conformément 
                  au Règlement Général sur la Protection des Données (RGPD).
                </p>
              </div>
            </div>

            {/* Privacy Sections */}
            <div className="space-y-6">
              {/* Section 1 */}
              <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-purple-100 p-6 md:p-8 hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">1</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mt-1">Données collectées</h2>
                </div>
                <div className="text-gray-700 leading-relaxed ml-14 space-y-3">
                  <p className="font-semibold text-gray-800">Nous collectons les informations suivantes :</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li><strong>Informations d'identification :</strong> nom, prénom, adresse email</li>
                    <li><strong>Informations de livraison :</strong> adresse postale, numéro de téléphone</li>
                    <li><strong>Informations de paiement :</strong> données de carte bancaire (cryptées et sécurisées)</li>
                    <li><strong>Données de navigation :</strong> adresse IP, type de navigateur, pages visitées</li>
                    <li><strong>Informations OAuth :</strong> données de profil Google (si connexion via Google)</li>
                  </ul>
                </div>
              </div>

              {/* Section 2 */}
              <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-purple-100 p-6 md:p-8 hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-pink-500 to-red-500 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">2</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mt-1">Utilisation des données</h2>
                </div>
                <div className="text-gray-700 leading-relaxed ml-14 space-y-3">
                  <p>Vos données personnelles sont utilisées pour :</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Traiter vos commandes et gérer les livraisons</li>
                    <li>Gérer votre compte client</li>
                    <li>Vous envoyer des confirmations de commande et des mises à jour</li>
                    <li>Améliorer nos services et votre expérience utilisateur</li>
                    <li>Respecter nos obligations légales et réglementaires</li>
                    <li>Prévenir la fraude et assurer la sécurité du site</li>
                  </ul>
                </div>
              </div>

              {/* Section 3 */}
              <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-purple-100 p-6 md:p-8 hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">3</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mt-1">Partage des données</h2>
                </div>
                <div className="text-gray-700 leading-relaxed ml-14 space-y-3">
                  <p>Nous ne vendons jamais vos données personnelles. Nous pouvons partager vos informations avec :</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li><strong>Prestataires de services :</strong> transporteurs, processeurs de paiement</li>
                    <li><strong>Partenaires technologiques :</strong> hébergement web, services d'analyse</li>
                    <li><strong>Autorités légales :</strong> si requis par la loi</li>
                  </ul>
                  <p className="mt-3">
                    Tous nos partenaires sont contractuellement tenus de protéger vos données et de les utiliser 
                    uniquement aux fins spécifiées.
                  </p>
                </div>
              </div>

              {/* Section 4 */}
              <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-purple-100 p-6 md:p-8 hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">4</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mt-1">Cookies et technologies similaires</h2>
                </div>
                <div className="text-gray-700 leading-relaxed ml-14 space-y-3">
                  <p>Nous utilisons des cookies pour améliorer votre expérience de navigation :</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li><strong>Cookies essentiels :</strong> nécessaires au fonctionnement du site</li>
                    <li><strong>Cookies de performance :</strong> pour analyser l'utilisation du site</li>
                    <li><strong>Cookies de personnalisation :</strong> pour mémoriser vos préférences</li>
                  </ul>
                  <p className="mt-3">
                    Vous pouvez gérer vos préférences de cookies dans les paramètres de votre navigateur.
                  </p>
                </div>
              </div>

              {/* Section 5 */}
              <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-purple-100 p-6 md:p-8 hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">5</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mt-1">Sécurité des données</h2>
                </div>
                <div className="text-gray-700 leading-relaxed ml-14 space-y-3">
                  <p>Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles :</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Cryptage SSL/TLS pour toutes les communications</li>
                    <li>Stockage sécurisé des mots de passe (hashage bcrypt)</li>
                    <li>Authentification OAuth 2.0 avec Google</li>
                    <li>Pare-feu et systèmes de détection d'intrusion</li>
                    <li>Sauvegardes régulières des données</li>
                    <li>Accès restreint aux données personnelles</li>
                  </ul>
                </div>
              </div>

              {/* Section 6 */}
              <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-purple-100 p-6 md:p-8 hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">6</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mt-1">Vos droits (RGPD)</h2>
                </div>
                <div className="text-gray-700 leading-relaxed ml-14 space-y-3">
                  <p>Conformément au RGPD, vous disposez des droits suivants :</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li><strong>Droit d'accès :</strong> obtenir une copie de vos données personnelles</li>
                    <li><strong>Droit de rectification :</strong> corriger vos données inexactes</li>
                    <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
                    <li><strong>Droit à la limitation :</strong> restreindre le traitement de vos données</li>
                    <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
                    <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
                  </ul>
                  <p className="mt-4 bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <strong>Pour exercer vos droits :</strong> contactez-nous à{" "}
                    <a href="mailto:privacy@jamaalstore.com" className="text-purple-600 hover:underline font-semibold">
                      privacy@jamaalstore.com
                    </a>
                  </p>
                </div>
              </div>

              {/* Section 7 */}
              <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-purple-100 p-6 md:p-8 hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">7</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mt-1">Conservation des données</h2>
                </div>
                <div className="text-gray-700 leading-relaxed ml-14 space-y-3">
                  <p>Nous conservons vos données personnelles :</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Pendant la durée de votre relation commerciale avec nous</li>
                    <li>Pour la durée nécessaire aux obligations légales (comptabilité, fiscalité)</li>
                    <li>Maximum 3 ans après votre dernière activité pour les données marketing</li>
                  </ul>
                  <p className="mt-3">
                    Après ces périodes, vos données sont supprimées ou anonymisées.
                  </p>
                </div>
              </div>

              {/* Section 8 */}
              <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-purple-100 p-6 md:p-8 hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">8</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mt-1">Transferts internationaux</h2>
                </div>
                <div className="text-gray-700 leading-relaxed ml-14 space-y-3">
                  <p>
                    Vos données peuvent être transférées et stockées sur des serveurs situés en dehors de votre pays. 
                    Dans ce cas, nous nous assurons que des garanties appropriées sont en place conformément au RGPD.
                  </p>
                </div>
              </div>

              {/* Section 9 */}
              <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-purple-100 p-6 md:p-8 hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">9</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mt-1">Modifications de la politique</h2>
                </div>
                <div className="text-gray-700 leading-relaxed ml-14 space-y-3">
                  <p>
                    Nous pouvons mettre à jour cette politique de confidentialité occasionnellement. 
                    Nous vous informerons de tout changement significatif par email ou via une notification sur le site.
                  </p>
                </div>
              </div>

              {/* Section 10 */}
              <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-purple-100 p-6 md:p-8 hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-gray-600 to-gray-800 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">10</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mt-1">Contact et réclamations</h2>
                </div>
                <div className="text-gray-700 leading-relaxed ml-14 space-y-4">
                  <p>Pour toute question concernant cette politique de confidentialité :</p>
                  <div className="bg-blue-300 from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <svg className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <div>
                          <span className="font-semibold text-gray-800">Email :</span>{" "}
                          <a href="mailto:privacy@jamaalstore.com" className="text-purple-600 hover:underline">
                            privacy@jamaalstore.com
                          </a>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <svg className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <div>
                          <span className="font-semibold text-gray-800">Délégué à la Protection des Données (DPO) :</span>{" "}
                          <a href="mailto:dpo@jamaalstore.com" className="text-purple-600 hover:underline">
                            dpo@jamaalstore.com
                          </a>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <p className="mt-4 text-sm bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <strong>📌 Note importante :</strong> Vous avez également le droit de déposer une réclamation 
                    auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) si vous estimez 
                    que vos droits ne sont pas respectés.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-12 text-center bg-blue-300 from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100">
              <p className="text-gray-600 mb-4">
                Votre confiance est notre priorité. Nous nous engageons à protéger vos données personnelles.
              </p>
              <a
                href="/"
                className="inline-block bg-yellow-500 from-purple-600 to-pink-600 text-white font-bold py-3 px-8 rounded-full hover:from-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-300 shadow-lg"
              >
                Retour à l'accueil
              </a>
            </div>
          </div>
        </div>
      </Layout>
    </Fragment>
  );
};

export default PrivacyPolicy;
